const MAX_DETAIL_LENGTH = 300

/**
 * @typedef {object} ApiResponseDetails
 * @property {string} endpoint
 * @property {number} status
 * @property {string} statusText
 * @property {string} message - the API's own error message, if any
 * @property {string} code - the API's own error code, if any
 */

/**
 * 4xx responses (`isClientError`) are reported to
 * Sentry as logs, everything else as errors.
 */
export class ApiResponseError extends Error {
  /**
   * @param {string} message
   * @param {ApiResponseDetails} details
   */
  constructor(message, details) {
    super(message)
    this.name = 'ApiResponseError'
    this.details = details
  }

  /** 4xx: the request was refused, the API itself is fine */
  get isClientError() {
    return this.details.status >= 400 && this.details.status < 500
  }
}

/**
 * Whether an error is a 4xx API response, which is logged instead of reported
 * as an error
 * @param {unknown} error
 * @returns {error is ApiResponseError}
 */
export const isApiClientError = (error) =>
  error instanceof ApiResponseError && error.isClientError

/**
 * @param {unknown} value
 * @returns {string}
 */
const toDetail = (value) => {
  if (value === undefined || value === null || value === '') return ''
  const text = typeof value === 'string' ? value : JSON.stringify(value)
  return text.slice(0, MAX_DETAIL_LENGTH)
}

/**
 * Reads a field off a parsed JSON value without trusting its shape
 * @param {unknown} value
 * @param {string} key
 * @returns {unknown}
 */
const getField = (value, key) =>
  typeof value === 'object' && value !== null
    ? /** @type {Record<string, unknown>} */ (value)[key]
    : undefined

/**
 * Reads what the API said about a failed request. Only the error fields of a
 * JSON body are kept
 *
 * @param {Response} response
 * @returns {Promise<{ message: string, code: string }>}
 */
const readErrorDetail = async (response) => {
  try {
    const text = (await response.clone().text()).trim()

    if (text.startsWith('{')) {
      /** @type {unknown} */
      const parsed = JSON.parse(text)
      // some endpoints wrap their payload in { original: {...} }
      const data = getField(parsed, 'original') || parsed
      const error = getField(data, 'error')
      return {
        message: toDetail(
          getField(data, 'message') || getField(error, 'message') || error,
        ),
        code: toDetail(getField(data, 'code') || getField(error, 'code')),
      }
    }

    if (text.startsWith('<')) {
      const title = text.match(/<title[^>]*>([^<]*)<\/title>/i)?.[1]
      return { message: toDetail(title?.trim()), code: '' }
    }

    return { message: toDetail(text), code: '' }
  } catch {
    return { message: '', code: '' }
  }
}

/**
 * Builds the message and details for a non-ok response
 *
 * @param {Response} response - the non-ok response
 * @param {string} endpoint - e.g. '/api/form-submit'
 * @returns {Promise<[string, ApiResponseDetails]>}
 */
async function readApiResponseError(response, endpoint) {
  const status = response.status || 0
  const statusText = response.statusText || ''
  const { message, code } = await readErrorDetail(response)

  return [
    `${endpoint} failed: ${status || 'unknown status'}${
      message ? ` - ${message}` : statusText ? ` ${statusText}` : ''
    }`,
    { endpoint, status, statusText, message, code },
  ]
}

/**
 * Builds the error for a non-ok API response
 *
 * @param {Response} response - the non-ok response
 * @param {string} endpoint - e.g. '/api/form-submit' or '/lop/flows/event'
 * @returns {Promise<ApiResponseError>}
 */
export async function createApiResponseError(response, endpoint) {
  return new ApiResponseError(
    ...(await readApiResponseError(response, endpoint)),
  )
}
